// ------------------------------------------------------
// TermsAndConditions.js
// ------------------------------------------------------

import React, { useEffect, useContext } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { Helmet } from 'react-helmet';
import { AppContext } from './AppContext';

import Header from './Header';
import Footer from './Footer';

// ---------------------------------------------
// Terms Data Object
// ---------------------------------------------
const termsData = {
    lastUpdated: '03/01/2025',
    businessOwner: 'Mr. Anindya Chowdhury',
    businessType: 'Sole Proprietorship',
    contactEmail: 'connect@buzztobrand.com',
    website: 'https://www.buzztobrand.com',
};

export default function TermsAndConditions() {
    const { settings } = useContext(AppContext);
    const brandName = settings?.brandName || 'Buzz To Brand';

    useEffect(() => {
        AOS.init({ once: true });
    }, []);

    const formattedDate = (() => {
        const [day, month, year] = termsData.lastUpdated.split('/');
        const dateObj = new Date(`${year}-${month}-${day}`);
        return dateObj.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });
    })();

    return (
        <>
            <Helmet>
                <title>Terms & Conditions | {brandName}</title>
                <meta
                    name="description"
                    content={`Read the Terms and Conditions governing the use of ${brandName}'s website and services.`}
                />
            </Helmet>

            <Header />

            <main
                className="p-6 lg:p-16 max-w-4xl mx-auto text-gray-800 leading-relaxed text-justify"
                data-aos="fade-up"
            >
                <h1 className="text-4xl font-bold mb-8 text-center">
                    Terms & Conditions
                </h1>

                <p className="text-sm text-gray-500 mb-10 italic text-center">
                    Last Updated: {formattedDate}
                </p>

                {/* ---------------------- INTRO ---------------------- */}
                <section className="mb-10">
                    <p>
                        Welcome to <strong>{brandName}</strong> (“Business”, “we”, “us”, or
                        “our”). The website{' '}
                        <a
                            href={termsData.website}
                            className="text-indigo-600 hover:underline"
                        >
                            {termsData.website}
                        </a>{' '}
                        (“Website”) is owned and operated by{' '}
                        <strong>{termsData.businessOwner}</strong>, operating as a{' '}
                        {termsData.businessType} based in West Bengal, India.
                    </p>
                    <p className="mt-4">
                        By accessing or using this Website or any services offered by Buzz To
                        Brand, you (“User”, “you”, or “your”) agree to be bound by these Terms
                        and Conditions (“Terms”). If you do not agree, you must discontinue
                        use immediately.
                    </p>
                </section>

                {/* ---------------------- SECTIONS ---------------------- */}
                <Section title="1. Acceptance of Terms">
                    These Terms, along with our Privacy Policy and any notices published on
                    the Website, constitute the entire agreement between you and Buzz To
                    Brand. Continued use of the Website confirms your acceptance.
                </Section>

                <Section title="2. Modification of Terms">
                    We may modify these Terms at any time without prior notice. Changes
                    become effective upon posting. Continued use constitutes acceptance
                    of the revised Terms.
                </Section>

                <Section title="3. User Obligations">
                    <ul className="list-disc ml-6 space-y-2">
                        <li>Use the Website only for lawful purposes.</li>
                        <li>No unauthorized copying or distribution of content.</li>
                        <li>No interference with Website functionality or security.</li>
                        <li>You must be at least 18 years of age.</li>
                    </ul>
                </Section>

                <Section title="4. Intellectual Property">
                    All content, materials, frameworks, designs, and visuals are the
                    intellectual property of Buzz To Brand unless stated otherwise.
                    Unauthorized use is prohibited.
                </Section>

                <Section title="5. Nature of Services and Disclaimer">
                    All services and content are provided for informational and strategic
                    purposes only. We do not guarantee outcomes, funding success, revenue,
                    or growth. Nothing constitutes legal, financial, tax, or investment
                    advice.
                </Section>

                <Section title="6. Payments and Refunds">
                    All fees are non-refundable unless explicitly agreed in writing.
                    Consulting services are time and effort based and deemed delivered
                    once initiated.
                </Section>

                <Section title="7. Scope of Services">
                    Services are limited to the scope agreed upon in writing. Additional
                    work may require separate fees and timelines.
                </Section>

                <Section title="8. Limitation of Liability">
                    <p>
                        To the maximum extent permitted by applicable law, Buzz To Brand shall not
                        be liable to you for any indirect, incidental, special, consequential,
                        exemplary, or punitive damages, including but not limited to loss of
                        profits, loss of revenue, loss of data, loss of business opportunity, or
                        reputational harm, arising out of or related to the Website or any services
                        provided.
                    </p>

                    <p className="mt-4">
                        In no event shall Buzz To Brand’s total aggregate liability to you for any
                        and all claims, demands, damages, losses, or causes of action, whether
                        arising in contract, tort (including negligence), or otherwise, exceed the
                        total fees actually received by Buzz To Brand from you in the thirty (30)
                        days immediately preceding the event giving rise to the claim.
                    </p>
                </Section>

                <Section title="9. Indemnification and Responsibility">
                    <p>
                        While Buzz To Brand makes commercially reasonable efforts to provide
                        thoughtful, good faith strategic guidance based on available information,
                        you acknowledge that all business, financial, and operational decisions
                        taken based on our Website, services, content, or recommendations are made
                        at your sole discretion and risk.
                    </p>

                    <p className="mt-4">
                        You agree to indemnify and hold Buzz To Brand harmless from any and all
                        claims, demands, damages, losses, liabilities, costs, or proceedings
                        (including reasonable legal fees) brought by any third party arising out of
                        or related to actions taken by you based on such guidance, your use of the
                        Website or services, unlawful conduct, or violation of these Terms.
                    </p>
                </Section>


                <Section title="10. Confidentiality">
                    Any non public information shared during an engagement will be treated
                    as confidential unless disclosure is required by law.
                </Section>

                <Section title="11. Third Party Services">
                    We are not responsible for third party platforms, tools, links, or
                    services referenced or integrated through the Website.
                </Section>

                <Section title="12. Termination">
                    We may suspend or terminate access to the Website or services at our
                    discretion for violations or misuse.
                </Section>

                <Section title="13. Force Majeure">
                    We are not liable for delays or failures caused by events beyond
                    reasonable control including technical failures or acts of government.
                </Section>

                <Section title="14. Governing Law and Jurisdiction">
                    These Terms are governed by the laws of India. Courts in West
                    Bengal shall have exclusive jurisdiction.
                </Section>

                <Section title="15. Contact Information">
                    For questions regarding these Terms, contact us at{' '}
                    <a
                        href={`mailto:${termsData.contactEmail}`}
                        className="text-indigo-600 hover:underline"
                    >
                        {termsData.contactEmail}
                    </a>
                    .
                </Section>

                <p className="text-center mt-12 font-medium">
                    YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREED TO BE BOUND
                    BY THESE TERMS AND CONDITIONS.
                </p>
            </main>

            <Footer />
        </>
    );
}

// ---------------------------------------------
// Reusable Section Component
// ---------------------------------------------
function Section({ title, children }) {
    return (
        <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">{title}</h2>
            <div>{children}</div>
        </section>
    );
}
